/**
 * @module app.cm
 */

import { autoinject, bindingMode, BindingEngine, Disposable, ICollectionObserverSplice } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { TaskService } from 'app/core/services/task-service';
import { QueueService } from 'app/core/services/queue-service';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { ApiDate } from 'app/shared/models/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AddTasksSelected extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    /** Array of tasks the users has selected (standard and custom) to be saved */
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected insertTasks: TaskWithLocalState[] = [];

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    protected filteredTasks: TaskWithLocalState[] = [];

    protected localTasks: TaskWithLocalState[] = [];
    protected localTasksSubscription: Disposable | null = null;

    @bindable.objectNull
    protected tasksConfig: ITasksConfig | null = null;

    @bindable.objectNull
    protected queuesConfig: IQueuesConfig | null = null;

    /** array of tasks user has selected from either the multi-select or autocomplete */
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected selectedTasks: Core.TaskDefinition[] = [];

    /** Used to show copy all on delegated to user if all queues are the same or blank */
    protected allQueuesBlankOrSame: boolean = false;

    /** queue lookup (bound to select) */
    protected queues: ReadonlyArray<Core.Queue> = [];

    /** Permission that controls whether a custom task can be added to the library (N/A to interventions) */
    protected hasAddCustomTaskToLibrary: boolean = false;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly taskService: TaskService,
        private readonly queueService: QueueService,
        private readonly bindingEngine: BindingEngine,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.tasksConfig) { throw new Error('Bindable property [tasks-config] was not set'); }
        if(!this.queuesConfig) { throw new Error('Bindable property [queues-config] was not set'); }

        this.taskService.configure(this.tasksConfig);
        this.queueService.configure(this.queuesConfig);

        this.checkPermissions();

        this.localTasks = this.filterTasks(this.insertTasks);
        this.loadLookups();
    }

    protected attached(): void {
        this.observeLocalTasks();
    }

    protected detached(): void {
        if(this.localTasksSubscription) {
            this.localTasksSubscription.dispose();
        }
    }


    ////****************** Events ***************************************************////
    protected delegateToChanged(changedTask: Core.Task | null): void {
        if(!changedTask?.delegated?.queue) { return; }

        const queue = changedTask.delegated.queue;
        if(!queue.delegated?.type) { return; }

        if(changedTask.delegated?.to) {
            changedTask.delegated.to.type = {
                id: queue.delegated.type.id,
                code: queue.delegated.type.code
            };
        }
    }

    protected async delegateQueueChanged(changedTask: Core.Task | null): Promise<void> {
        if(this.insertTasks.length === 0) { this.allQueuesBlankOrSame = true; }
        let firstSelectedQueue: Core.Queue | null = null;
        if(this.insertTasks[0]?.delegated?.queue) {
            firstSelectedQueue = this.insertTasks[0].delegated.queue || null;
        }

        if(firstSelectedQueue) {
            this.allQueuesBlankOrSame = this.insertTasks.every(t => {
                const queueId = t.delegated?.queue?.id;
                return queueId === firstSelectedQueue!.id || !queueId;
            });
        }
        else {
            this.allQueuesBlankOrSame = false;
        }

        if(changedTask?.delegated?.to) {
            changedTask.delegated.to = null;
        }
        if(changedTask?.delegated?.queue?.id && changedTask.delegated.queue.users === undefined) {
            //WARNING: even if the queue's type is Intervention, if the queue has been allowed to "All Authenticated Users" -
            //user can in theory send this Intervention to some user without CM access.
            //This is up to the client to tweak.
            changedTask.delegated.queue.users = await this.loadQueueUsers(changedTask?.delegated?.queue?.id);
        }
    }

    protected async localTasksChanged(splices: ICollectionObserverSplice<TaskWithLocalState>[]): Promise<void> {
        // Return if no tasks selected or if a task was removed
        if(this.localTasks.length === 0 || splices[0].removed.length) return;

        // Traverse over all recently added tasks and load queue users for each
        const startingIndex = splices[0].index;
        for(let i = startingIndex; i < this.localTasks.length; i++) {
            if(this.localTasks[i].delegated?.queue?.id) {
                const queueId = this.localTasks[i].delegated?.queue?.id;
                const users = await this.loadQueueUsers(queueId!);
                this.localTasks[i].delegated!.queue!.users = users;
            }
        }
    }


    ////****************** Functions ************************************************////
    protected observeLocalTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.localTasksSubscription) {
            this.localTasksSubscription.dispose();
        }
        this.localTasksSubscription = this.bindingEngine
            .collectionObserver(
                this.localTasks)
            .subscribe(
                this.localTasksChanged
                    .bind(this));

        // subscription disposed of in detached()
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CaseTask.Insert.AddToLibrary');
    }

    /** Called when a user removes a task from the grid. Will ultimately trigger the selectedTasksChanged() method */
    protected removeTask(task: Core.Task): void {
        if(task.isCustom) {
            // Custom tasks are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertTasks.indexOf(task);
            if(index >= 0) {
                this.insertTasks.splice(index, 1);
            }
        }
        else {
            // Find the task in the selectedTasks or selectedRecommendedTasks array whence to remove.
            // Either will trigger the selectedTasksChanged() or selectedRecommendedTasksChanged()), which will update insertTasks
            let st = this.selectedTasks.find(t => t.id === (task.definition && task.definition.id));
            if(st) {
                this.selectedTasks.splice(this.selectedTasks.indexOf(st), 1);
            }
        }

        this.localTasks = this.filterTasks(this.insertTasks);
    }

    protected copyDelegatedQueue(queue: Core.Queue | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {

            if(!t.delegated) {
                t.delegated = {};
            }
            t.delegated.queue = queue;
            t.delegated.to = null;
        });

        this.allQueuesBlankOrSame = true;
    }

    protected copyDelegatedTo(delegateTo: Core.TaskDelegateTargetModel | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {
            if(!t.delegated) {
                t.delegated = {};
            }
            if(t.delegated.queue) {
                t.delegated.to = delegateTo;
                this.delegateToChanged(t);
            }
        });
    }

    protected copyDueDate(date: ApiDate | null): void {
        this.filterTasks(this.insertTasks).forEach(t => {

            if(!t.due) {
                t.due = {};
            }
            t.due.date = date;
        });
    }

    public filterTasks(tasks: Core.Task[]): Core.Task[] {
        if(!this.filteredTasks || this.filteredTasks.length === 0) return tasks;
        return tasks.filter(t => this.filteredTasks.some(ft => ft.externalIdentifier === t.externalIdentifier));
    }


    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.queueService.loadQueues(null, { all: true }, ['type']);
            this.queues = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadQueueUsers(queueId: string): Promise<Array<Core.User>> {
        try {
            this.state.beginLoad();
            const options: Partial<IQueryLoadOptions> = { all: true };
            const response = await this.queueService.loadQueueUsers(queueId, options);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
